import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';
import { CSSTransition } from 'react-transition-group';

import LoadMask from 'components/LoadMask';
import BasicButton from 'components/BasicButton';
import HorizontalLoader from 'components/HorizontalLoader';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicSubmitButton from 'components/BasicSubmitButton';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import SendMessageRestrictionDialog from 'scenes/Dialog/components/SendMessageRestrictionDialog';
import SendMessageFormSubjectField from 'scenes/Form/components/SendMessageForm/components/SendMessageFormSubjectField';
import SendMessageFormMessageField from 'scenes/Form/components/SendMessageForm/components/SendMessageFormMessageField';
import SendMessageFormAttachmentsFieldList from 'scenes/Form/components/SendMessageForm/components/SendMessageFormAttachmentsFieldList';
import SendMessageFormTemplateSelect from 'scenes/Form/components/SendMessageForm/components/SendMessageFormTemplateSelect';
import SendMessageFormRecipientSelect from 'scenes/Form/components/SendMessageForm/components/SendMessageFormRecipientSelect';

import SendMessageFormValidationSchema from 'scenes/Form/components/SendMessageForm/validate';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation';
import { MESSAGE_TYPE } from 'constants/message-types';
import { REVIEW_TYPES } from 'constants/review-types';
import { ERROR_CODE } from 'constants/error-codes';
import { ENDPOINT } from 'constants/endpoints';
import { useApi } from 'hooks/useApi';
import {
  convertEditorStateToHtml,
  convertHtmlToEditorState,
} from 'helpers/formik';

const SendMessageForm = props => {
  const {
    actions: { proceed, dismiss, handleError },
    params: {
      id = null,
      subject = '',
      message = '',
      recipients,
      messageType,
      formType = 'default',
      upgradeRequestEmail,
      review,
      hasTemplateSelection = false,
      hasRecipientsSelection = false,
    },
  } = props;

  const [formData, setFormData] = useState(
    MESSAGE_TYPE.TYPE_INBOUNDS_REPLY === messageType
      ? {
          isFetching: true,
          subject: 'InBound Reply',
          message: convertHtmlToEditorState('Thank you for your submission.'),
          attachments: [],
          attachmentsSize: 0,
          recipients: recipients
            ? isArray(recipients)
              ? recipients.join(',')
              : recipients
            : '',
        }
      : {
          isFetching: true,
          subject,
          message: convertHtmlToEditorState(message || ''),
          attachments: [],
          attachmentsSize: 0,
          recipients: recipients
            ? isArray(recipients)
              ? recipients.join(',')
              : recipients
            : '',
        }
  );
  const api = useApi();

  useEffect(() => {
    if (id) {
      api.get(
        ENDPOINT.FORM.GET.INBOX_MESSAGE,
        { id, formType },
        {
          handleError,
          handleSuccess: formData => {
            setFormData(state => ({
              ...state,
              subject: formData.fields.subject,
              message: convertHtmlToEditorState(formData.fields.message),
              isFetching: false,
            }));
          },
        }
      );
    } else {
      setFormData(state => ({ ...state, isFetching: false }));
    }
  }, []);

  const handleSubmit = (values, form) => {
    const queryParams = {
      recipients: hasRecipientsSelection
        ? values.recipients.value
        : values.recipients,
      subject: values.subject,
      message: convertEditorStateToHtml(values.message),
      files: values.attachments.map(attach => attach.file),
    };

    if (messageType) {
      queryParams.type = messageType;
    }

    if (messageType === MESSAGE_TYPE.TYPE_REVIEW_APPEAL) {
      if (review.ReviewType === REVIEW_TYPES.OVERVIEW) {
        queryParams.reviewId = review.OverviewID;
      }
      if (review.ReviewType === REVIEW_TYPES.INTERVIEW) {
        queryParams.reviewId = review.InterviewID;
      }
      if (review.ReviewType === REVIEW_TYPES.EMPLOYEE) {
        queryParams.reviewId = review.ReviewID;
      }

      queryParams.reviewType = review.ReviewType;
    }

    api.post(ENDPOINT.ACTION.SEND_MESSAGE, queryParams, {
      handleError: response => handleSubmissionErrors(response, form, values),
      handleSuccess: ({ message }) => proceed(message),
    });
  };

  const handleSubmissionErrors = (
    response,
    { setErrors, setFieldError, setSubmitting },
    values
  ) => {
    const { code = null, limit = null, files = null } = response.data;
    if (code && code === ERROR_CODE.RECIPIENTS_LIMIT_EXCEEDED && limit) {
      dismiss();
      SendMessageRestrictionDialog({ limit, upgradeRequestEmail });
    } else if (files && typeof files === 'string') {
      values.attachments.forEach((f, index) => {
        setFieldError('attachments[' + index + ']', files);
      });
      setSubmitting(false);
    } else if (files && typeof files === 'object') {
      Object.keys(files).forEach(f => {
        const fieldName = `attachments[${f.replace(/\D+/g, '')}]`;
        setFieldError(fieldName, files[f]);
      });
      setSubmitting(false);
    } else {
      setSubmitting(false);
      handleError(response, setErrors);
    }
  };

  return formData.isFetching ? (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    </div>
  ) : (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={{ ...formData }}
          validationSchema={SendMessageFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {form => (
            <form
              onSubmit={form.handleSubmit}
              className="form__content"
              style={{ maxWidth: '100%' }}
            >
              {form.isSubmitting && <LoadMask />}

              {hasRecipientsSelection && (
                <div
                  className="form__group form__group--compact"
                  style={{ marginBottom: '25px' }}
                >
                  <SendMessageFormRecipientSelect name="recipients" />
                </div>
              )}

              {hasTemplateSelection && (
                <div
                  className="form__group form__group--compact"
                  style={{ marginBottom: '25px' }}
                >
                  <SendMessageFormTemplateSelect
                    name="template"
                    onRequestFail={response => {
                      dismiss();
                      ResponseErrorDialog({ response });
                    }}
                    onBeforeSelect={() => form.setSubmitting(true)}
                    onAfterSelect={() => form.setSubmitting(false)}
                  />
                </div>
              )}

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '25px' }}
              >
                <SendMessageFormSubjectField name="subject" />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ maxWidth: '100%', marginBottom: '25px' }}
              >
                <SendMessageFormMessageField name="message" />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ maxWidth: '100%', marginBottom: '25px' }}
              >
                <SendMessageFormAttachmentsFieldList
                  name="attachments"
                  form={form}
                />
              </div>

              {isArray(recipients) && (
                <h4 className="dialog__send-list">
                  SELECTED RECIPIENTS:{' '}
                  <span className="dialog__send-counter">
                    {recipients.length}
                  </span>
                </h4>
              )}

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup
                  groupStyles={{ width: '100%', justifyContent: 'center' }}
                >
                  <BasicSubmitButton
                    controlFunc={form.handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Send"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  );
};

SendMessageForm.propTypes = {
  actions: PropTypes.shape({
    proceed: PropTypes.func,
    dismiss: PropTypes.func,
    handleError: PropTypes.func,
  }),
  params: PropTypes.shape({
    id: PropTypes.number,
    subject: PropTypes.string,
    message: PropTypes.string,
    recipients: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    messageType: PropTypes.string,
    formType: PropTypes.oneOf(['reply', 'forward', 'default']),
    upgradeRequestEmail: PropTypes.string,
    review: PropTypes.object,
    hasTemplateSelection: PropTypes.bool,
    hasRecipientsSelection: PropTypes.bool,
  }),
};

export default SendMessageForm;
